import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Notifications } from '@mui/icons-material';
import CursorTooltip from './CursorTooltip';
import apiService from '../services/api';
import { normalizeAppPath } from '../utils/appDeepLinks';

const POLL_MS = 60000;

const formatNotificationTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [panelPosition, setPanelPosition] = useState(null);

  const refreshUnread = useCallback(async () => {
    try {
      const response = await apiService.getInboxUnreadCount();
      setUnreadCount(Number(response?.data?.unreadCount || 0));
    } catch {
      // Keep the last known count if the poll fails.
    }
  }, []);

  const loadInbox = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await apiService.getInboxNotifications(20);
      setItems(Array.isArray(response?.data?.items) ? response.data.items : []);
      setUnreadCount(Number(response?.data?.unreadCount || 0));
    } catch {
      setLoadError('Could not load notifications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let timer = null;
    const start = () => {
      if (timer) return;
      refreshUnread();
      timer = setInterval(refreshUnread, POLL_MS);
    };
    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stop();
        return;
      }
      start();
    };

    if (document.visibilityState !== 'hidden') {
      start();
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshUnread]);

  useEffect(() => {
    if (open) {
      loadInbox();
    }
  }, [open, loadInbox]);

  const updatePanelPosition = useCallback(() => {
    if (!buttonRef.current) {
      setPanelPosition(null);
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    const width = Math.min(380, window.innerWidth - 16);
    let left = rect.right - width;
    if (left < 8) left = 8;
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }
    const top = rect.bottom + 8;
    setPanelPosition({ top, left, width });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPanelPosition(null);
      return undefined;
    }
    updatePanelPosition();
    const onScrollOrResize = () => updatePanelPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (buttonRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const handleToggle = () => setOpen((prev) => !prev);

  const handleOpenItem = async (item) => {
    setItems((prev) => prev.map((row) => (
      row.id === item.id ? { ...row, readAt: row.readAt || new Date().toISOString() } : row
    )));
    if (!item.readAt) {
      setUnreadCount((count) => Math.max(0, count - 1));
      apiService.markInboxNotificationRead(item.id).catch(() => {});
    }
    setOpen(false);
    navigate(normalizeAppPath(item.linkUrl || '/'));
  };

  const handleMarkAllRead = async () => {
    const previous = items;
    setItems((prev) => prev.map((row) => (
      row.readAt ? row : { ...row, readAt: new Date().toISOString() }
    )));
    setUnreadCount(0);
    try {
      await apiService.markAllInboxNotificationsRead();
    } catch {
      setItems(previous);
      refreshUnread();
    }
  };

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <>
      <CursorTooltip title={unreadCount ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}` : 'Notifications'} disabled={open}>
        <button
          ref={buttonRef}
          type="button"
          className="header-notification-bell"
          aria-label="Notifications"
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={handleToggle}
        >
          <Notifications sx={{ fontSize: 22 }} />
          {unreadCount > 0 ? (
            <span className="header-notification-bell__badge">{badgeLabel}</span>
          ) : null}
        </button>
      </CursorTooltip>
      {open && panelPosition && createPortal(
        <div
          ref={panelRef}
          className="notification-bell-panel"
          role="dialog"
          aria-label="Notifications"
          style={{
            position: 'fixed',
            top: panelPosition.top,
            left: panelPosition.left,
            width: panelPosition.width,
            zIndex: 12000
          }}
        >
          <div className="notification-bell-panel__header">
            <strong>Notifications</strong>
            {unreadCount > 0 ? (
              <button
                type="button"
                className="notification-bell-panel__mark-all"
                onClick={handleMarkAllRead}
              >
                Mark all read
              </button>
            ) : null}
          </div>
          <div className="notification-bell-panel__list">
            {loadError ? (
              <div className="notification-bell-panel__empty notification-bell-panel__empty--error">
                {loadError}
              </div>
            ) : null}
            {loading && items.length === 0 ? (
              <div className="notification-bell-panel__empty">Loading notifications…</div>
            ) : items.length === 0 && !loadError ? (
              <div className="notification-bell-panel__empty">
                No notifications yet. Alerts you receive by email will also show up here.
              </div>
            ) : (
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`notification-bell-panel__item${item.readAt ? '' : ' notification-bell-panel__item--unread'}`}
                  onClick={() => handleOpenItem(item)}
                >
                  <span className="notification-bell-panel__item-title">{item.title}</span>
                  {item.body ? (
                    <span className="notification-bell-panel__item-body">{item.body}</span>
                  ) : null}
                  <span className="notification-bell-panel__item-time">
                    {formatNotificationTime(item.createdAt)}
                  </span>
                </button>
              ))
            )}
          </div>
          <button
            type="button"
            className="notification-bell-panel__footer"
            onClick={() => {
              setOpen(false);
              navigate('/profile');
            }}
          >
            Manage email alerts in Profile
          </button>
        </div>,
        document.body
      )}
    </>
  );
};

export default NotificationBell;
